import {
  buildClearWeightGoal,
  buildDeleteWeight,
  buildLogWeight,
  buildSetWeightGoal,
  buildSetWeightUnit,
  newId,
  type BuildCtx,
  type LocalDate,
  type WeightEntryLike,
  type WeightInput,
  type WeightUnit,
} from '@tahi/core';
import { monotonicNow } from '../mutation';
import * as repo from '../repos/weight';
import { runCommand, type CommandEnv } from './dispatch';

export type WeightCommandResult<T = object> = ({ ok: true } & T) | { ok: false; error: string };

function ctx(env: CommandEnv): BuildCtx {
  return { now: monotonicNow(), tz: env.tz, newId };
}

/**
 * Log or replace a day's weight. Returns the entry it replaced (null for a new day), so the
 * caller's Undo can put it back.
 */
export async function logWeight(
  env: CommandEnv,
  input: WeightInput,
): Promise<WeightCommandResult<{ entry: WeightEntryLike; replaced: WeightEntryLike | null }>> {
  const before = await repo.getWeightEntry(env.db, env.userId, input.date);
  const r = buildLogWeight(env.userId, input, ctx(env));
  if (!r.ok) return r;
  await runCommand(env, { mutations: [r.mutation], event: r.event });
  return { ok: true, entry: r.entry, replaced: before && !before.deletedAt ? before : null };
}

/** Undo of a log: the replaced entry comes back, or a new day's entry is removed. */
export async function undoLogWeight(
  env: CommandEnv,
  date: LocalDate,
  replaced: WeightEntryLike | null,
): Promise<WeightCommandResult> {
  if (replaced) return restoreWeight(env, replaced);
  return deleteWeight(env, date);
}

export async function deleteWeight(env: CommandEnv, date: LocalDate): Promise<WeightCommandResult> {
  const r = buildDeleteWeight(await repo.getWeightEntry(env.db, env.userId, date), ctx(env));
  if (!r.ok) return r;
  await runCommand(env, { mutations: [r.mutation] });
  return { ok: true };
}

/** Undo of a delete: the same values logged again restore the soft-deleted row. */
export async function restoreWeight(
  env: CommandEnv,
  entry: WeightEntryLike,
): Promise<WeightCommandResult> {
  const r = buildLogWeight(
    env.userId,
    { date: entry.date, valueG: entry.valueG, unit: entry.unit, note: entry.note },
    ctx(env),
  );
  if (!r.ok) return r;
  await runCommand(env, { mutations: [r.mutation], event: r.event });
  return { ok: true };
}

export async function setWeightUnit(
  env: CommandEnv,
  unit: WeightUnit,
): Promise<WeightCommandResult> {
  const r = buildSetWeightUnit(env.userId, unit, ctx(env));
  if (!r.ok) return r;
  await runCommand(env, { mutations: [r.mutation] });
  return { ok: true };
}

export async function setWeightGoal(
  env: CommandEnv,
  input: { goalG: number; startG: number },
): Promise<WeightCommandResult> {
  const r = buildSetWeightGoal(env.userId, input, ctx(env));
  if (!r.ok) return r;
  await runCommand(env, { mutations: [r.mutation] });
  return { ok: true };
}

export async function clearWeightGoal(env: CommandEnv): Promise<void> {
  await runCommand(env, { mutations: [buildClearWeightGoal(env.userId, ctx(env))] });
}
